import { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { getTranslationsView } from 'modules/archive';
import { useI18n } from 'modules/i18n';
import { useTimeQueryString } from 'modules/query-string';
import { usePathBase, useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { PLAYBACK_RATES } from '../constants';
import {
    useGetMediaMissingText,
    useMediaPlayerResize,
    usePosterImage,
    useTimecodeFormat,
} from '../hooks';
import '../plugins/configurationMenuPlugin.js';
import '../plugins/customSkipButtonsPlugin.js';
import '../plugins/fractionalCurrentTimePlugin.js';
import '../plugins/toggleSizeButtonPlugin.js';
import { updatePlaybackRate } from '../redux/actions';
import { getPlaybackRate } from '../redux/selectors';
import {
    getQualityLabel,
    humanTimeToSeconds,
    mediaStreamsToSources,
} from '../utils';
import VideoJS from './VideoJS';

const KEYCODE_F = 70;
const KEYCODE_M = 77;
const KEYCODE_P = 80;
const KEYCODE_SPACE = 32;
const KEYCODE_LEFT = 37;
const KEYCODE_RIGHT = 39;
const KEYCODE_UP = 38;
const KEYCODE_DOWN = 40;

const FORWARD_STEP = 5;
const BACKWARD_STEP = 5;

const READY_STATE_HAVE_CURRENT_DATA = 2;

/**
 * Plays interview media using project playback settings and Redux playback state.
 *
 * @param {Object} props - Interview and playback controls.
 * @param {string} props.archiveId - Public interview identifier used in media routes.
 * @param {string} [props.className] - Additional wrapper classes.
 * @param {Object} props.interview - Interview media and subtitle metadata.
 * @param {number} [props.tape] - Selected tape number.
 * @param {boolean} [props.isPlaying] - Requested playback state.
 * @param {boolean} [props.timeChangeRequestAvailable] - Whether a seek is pending.
 * @param {number} [props.timeChangeRequest] - Requested time in seconds.
 * @param {Function} props.updateIsPlaying - Records playback state.
 * @param {Function} props.updateMediaTime - Records playback position.
 * @param {Function} props.resetMedia - Resets playback state on unmount.
 * @param {Function} props.sendTimeChangeRequest - Requests a tape and playback time.
 * @param {Function} props.clearTimeChangeRequest - Acknowledges a pending seek.
 * @returns {React.ReactElement} Media player, missing-media text, or project request status.
 */
export default function MediaElement({
    archiveId,
    className,
    interview,
    tape,
    isPlaying,
    timeChangeRequestAvailable,
    timeChangeRequest,
    updateIsPlaying,
    updateMediaTime,
    resetMedia,
    sendTimeChangeRequest,
    clearTimeChangeRequest,
}) {
    const pathBase = usePathBase();
    const { t, locale } = useI18n();
    const { project, isLoading, error } = useProject({ include: ['playback'] });
    const mediaStreams = project?.media_streams;
    const hasPlayback = !!project && !isLoading && !error;
    const playerRef = useRef(null);
    const dispatch = useDispatch();
    const playbackRate = useSelector(getPlaybackRate);
    const tapeRef = useRef(tape); // Used for event handler below.
    const isTranslationsView = useSelector(getTranslationsView);
    const mediaMissingText = useGetMediaMissingText();

    const { tape: tapeParam, time: timeParam } = useTimeQueryString();

    // Hook for manual resize via drag handle
    const { resizeHandleRef, isDragging } = useMediaPlayerResize();

    const timecodeFormat = useTimecodeFormat(interview);

    const aspectRatio = project
        ? `${project.aspect_x}:${project.aspect_y}`
        : undefined;
    const initialSources =
        mediaStreams &&
        mediaStreamsToSources(
            Object.values(mediaStreams),
            pathBase,
            interview.media_type,
            archiveId,
            interview.tape_count,
            tape
        );

    const { posterUrl } = usePosterImage(interview);

    const videoJsOptions = {
        autoplay: false,
        controls: true,
        responsive: false,
        fluid: false,
        aspectRatio,
        language: locale,
        sources: initialSources,
        poster: posterUrl,
        playbackRates: PLAYBACK_RATES,
        enableSourceset: false,
        controlBar: {
            volumePanel: {
                inline: false,
                volumeControl: {
                    vertical: true,
                },
            },
            children: [
                'progressControl',
                'skipBackward',
                'playToggle',
                'skipForward',
                {
                    name: 'FractionalCurrentTimeDisplay',
                    timecodeFormat,
                },
                'volumePanel',
                'playbackRateMenuButton',
                'qualitySelector',
                'subsCapsButton',
                'fullscreenToggle',
            ],
            skipButtons: {
                forward: FORWARD_STEP,
                backward: BACKWARD_STEP,
            },
        },
        userActions: {
            click: true,
            doubleClick: true,
            hotkeys: handleKeyPress,
        },
    };

    function handleKeyPress(event) {
        let newTime, newVolume;

        switch (event.which) {
            case KEYCODE_F:
                if (this.isFullscreen()) {
                    this.exitFullscreen();
                } else {
                    this.requestFullscreen();
                }
                break;
            case KEYCODE_M:
                if (this.muted()) {
                    this.muted(false);
                } else {
                    this.muted(true);
                }
                break;
            case KEYCODE_P:
            case KEYCODE_SPACE:
                if (this.paused()) {
                    this.play();
                } else {
                    this.pause();
                }
                break;
            case KEYCODE_LEFT:
                newTime = this.currentTime() - BACKWARD_STEP;
                if (newTime < 0) {
                    newTime = 0;
                }
                this.currentTime(newTime);
                break;
            case KEYCODE_RIGHT:
                newTime = this.currentTime() + FORWARD_STEP;
                this.currentTime(newTime);
                break;
            case KEYCODE_UP:
                newVolume = this.volume() + 0.1;
                if (newVolume > 1) {
                    newVolume = 1;
                }
                this.volume(newVolume);
                break;
            case KEYCODE_DOWN:
                newVolume = this.volume() - 0.1;
                if (newVolume < 0) {
                    newVolume = 0;
                }
                this.volume(newVolume);
                break;
            default:
        }
    }

    // Update the player's playback rate when it changes in Redux state.
    useEffect(() => {
        const player = playerRef.current;
        if (player && player.playbackRate() !== playbackRate) {
            player.playbackRate(playbackRate);
        }
    }, [playbackRate]);

    // Update the player's poster when it changes
    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        // Only update if the poster has changed and player is ready
        player.poster(posterUrl);
    }, [posterUrl]);

    // Check if time params exist in query string.
    useEffect(() => {
        const numTapes = Number.parseInt(interview.tape_count);

        if (tapeParam && timeParam) {
            try {
                const timeInSeconds = humanTimeToSeconds(timeParam);
                // TODO: Validate tape and time params.
                sendTimeChangeRequest(
                    Math.max(Math.min(tapeParam, numTapes), 1),
                    timeInSeconds
                );
            } catch (e) {
                console.warn('Invalid time format, skipping');
            }
        }
    }, []);

    // Reset media position on unmount.
    useEffect(() => {
        return resetMedia;
    }, []);

    // When media becomes unavailable, drop any stale player instance reference.
    useEffect(() => {
        if (!hasPlayback || interview.media_missing) {
            playerRef.current = null;
        }
    }, [hasPlayback, interview.media_missing]);

    // Update sources and tracks if tape has been changed.
    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return;
        }

        tapeRef.current = tape;

        const newSources = mediaStreamsToSources(
            Object.values(mediaStreams),
            pathBase,
            interview.media_type,
            archiveId,
            interview.tape_count,
            tape
        );

        player.src(newSources);

        addTextTracks();
    }, [tape, interview.transcript_coupled]);

    // Check if time has been changed from outside of component.
    // Also syncs Redux isPlaying=false → pauses the actual player.
    // Runs on every render so that tape and time params are always recognised.
    useEffect(() => {
        if (interview.media_missing) return;
        checkForTimeChangeRequest();
    });

    // Update text track labels when locale changes
    useEffect(() => {
        if (playerRef.current && interview.transcript_coupled) {
            addTextTracks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale, interview.transcript_coupled]);

    function addTextTracks() {
        if (!interview.transcript_coupled) return;

        const player = playerRef.current;
        if (!player) {
            return;
        }

        // Remove old text tracks, if any.
        const tracks = player.remoteTextTracks();
        for (let i = tracks.length - 1; i >= 0; i--) {
            player.removeRemoteTextTrack(tracks[i]);
        }

        // Use tape number from ref since this function is called from event handler.
        const actualTape = tapeRef.current;

        // Only add text tracks for languages with actual transcript data
        const subtitleLanguages = interview.alpha3s_with_transcript || [];

        const newTracks = subtitleLanguages.map((lang) => ({
            src: `${pathBase}/interviews/${archiveId}.vtt?lang=${lang}&tape_number=${actualTape}`,
            language: lang, // 3-letter language code
            kind: 'captions',
            label: isTranslationsView ? lang : t(lang), // Show translation key if translations view is enabled
        }));
        newTracks.forEach((newTrack) => {
            player.addRemoteTextTrack(newTrack, false);
        });
    }

    function checkForTimeChangeRequest() {
        if (interview.media_missing) return;

        // We use Redux as an event system here.
        // If a request is available, it is immediately cleared and processed.
        const player = playerRef.current;
        if (!player) {
            return;
        }

        // Sync Redux isPlaying state → actual player state.
        // Bidirectional: pause when Redux says stop, play when Redux says go.
        // Skip the play sync when a time change request is pending — the
        // request handler below will call play() after seeking to avoid
        // starting playback at a stale position.
        if (!isPlaying && !player.paused()) {
            player.pause();
        }
        if (isPlaying && player.paused() && !timeChangeRequestAvailable) {
            if (player.readyState() >= READY_STATE_HAVE_CURRENT_DATA) {
                player.play();
            } else {
                player.autoplay(true);
            }
        }

        if (timeChangeRequestAvailable) {
            clearTimeChangeRequest();

            player.currentTime(timeChangeRequest);

            // Only auto-play if it was already playing; respect pause state
            if (isPlaying) {
                if (player.readyState() >= READY_STATE_HAVE_CURRENT_DATA) {
                    player.play();
                } else {
                    player.autoplay(true);
                }
            }
        }
    }

    function handlePlayEvent() {
        updateIsPlaying(true);
    }

    function handlePauseEvent() {
        updateIsPlaying(false);
    }

    function handleTimeUpdateEvent() {
        const time = playerRef.current.currentTime();
        const roundedTime = Math.round(time * 10) / 10;
        updateMediaTime(roundedTime);
    }

    function handleEndedEvent() {
        if (tape >= Number(interview.tape_count)) {
            return;
        }

        sendTimeChangeRequest(tape + 1, 0);
    }

    function handleContextMenuEvent(e) {
        e.preventDefault();
        return false;
    }

    function handleQualitySelected() {
        /*
         * Add text tracks again after quality is selected.
         * Otherwise, they are lost.
         */
        addTextTracks();
    }

    function handlePlayerReady(player) {
        playerRef.current = player;
        window.mainPlayerInstance = player;
        player.playbackRate(playbackRate);
        player.on('ratechange', () => {
            dispatch(updatePlaybackRate(player.playbackRate()));
        });

        player.configurationMenuPlugin();
        player.toggleSizePlugin({
            buttonTitle:
                player.pluginTranslations?.toggleSize ||
                t('media_player.toggle_size_button'),
        });

        // Use centralized plugin translations from the hook
        player.customSkipButtonsPlugin({
            translations: player.pluginTranslations || {
                skipBack: t('media_player.skip_backwards'),
                skipForward: t('media_player.skip_forwards'),
            },
        });

        const qualities = player
            .currentSources()
            .map(getQualityLabel)
            .filter(Boolean);

        player.configurationMenuPlugin({
            playbackRates: videoJsOptions.playbackRates,
            qualities: qualities,
            translations: player.pluginTranslations || {
                playbackRate: t('media_player.playback_rate'),
                playbackQuality: t('media_player.playback_quality'),
            },
        });

        player.ready(() => {
            const configControl = player.controlBar.getChild(
                'ConfigurationControl'
            );
            const playbackRateMenuButton = player.controlBar.getChild(
                'playbackRateMenuButton'
            );
            const qualitySelector =
                player.controlBar.getChild('qualitySelector');
            const toggleSizeButton =
                player.controlBar.getChild('ToggleSizeButton');
            const subsCapsButton = player.controlBar.getChild('subsCapsButton');

            const currentSize = toggleSizeButton
                ? toggleSizeButton.currentPlayerSize
                : 'medium';

            // Disable subtitles if start size is small
            if (currentSize === 'small' && subsCapsButton) {
                subsCapsButton.disable();
                const textTracks = player.textTracks();
                for (let i = 0; i < textTracks.length; i++) {
                    if (textTracks[i].mode === 'showing') {
                        textTracks[i].mode = 'disabled';
                    }
                }
            }

            if (player.isFullscreen()) {
                if (configControl) configControl.hide();
                if (playbackRateMenuButton) playbackRateMenuButton.show();
                if (qualitySelector) qualitySelector.show();
                if (toggleSizeButton) toggleSizeButton.hide();

                // Enable subtitles button in fullscreen
                if (subsCapsButton) {
                    subsCapsButton.enable();

                    // If toggleSizeButton exists and has saved subtitles, restore them
                    if (
                        toggleSizeButton &&
                        toggleSizeButton.subtitleTrackWasEnabled
                    ) {
                        toggleSizeButton.restoreActiveSubtitleTrack();
                    }
                }
            } else {
                if (configControl) configControl.show();
                if (playbackRateMenuButton) playbackRateMenuButton.hide();
                if (qualitySelector) qualitySelector.hide();
                if (toggleSizeButton) toggleSizeButton.show();

                // When exiting fullscreen, check if we need to disable subtitles
                if (
                    toggleSizeButton &&
                    toggleSizeButton.currentPlayerSize === 'small'
                ) {
                    if (subsCapsButton) {
                        // Save current subtitle state before disabling
                        if (toggleSizeButton.saveActiveSubtitleTrack) {
                            toggleSizeButton.saveActiveSubtitleTrack();
                        }

                        subsCapsButton.disable();

                        // Disable active subtitle tracks
                        const textTracks = player.textTracks();
                        for (let i = 0; i < textTracks.length; i++) {
                            if (textTracks[i].mode === 'showing') {
                                textTracks[i].mode = 'disabled';
                            }
                        }
                    }
                }
            }
        });

        player.on('fullscreenchange', () => {
            const configControl = player.controlBar.getChild(
                'ConfigurationControl'
            );
            const playbackRateMenuButton = player.controlBar.getChild(
                'playbackRateMenuButton'
            );
            const qualitySelector =
                player.controlBar.getChild('qualitySelector');
            const toggleSizeButton =
                player.controlBar.getChild('ToggleSizeButton');

            if (player.isFullscreen()) {
                if (configControl) configControl.hide();
                if (playbackRateMenuButton) playbackRateMenuButton.show();
                if (qualitySelector) qualitySelector.show();
                if (toggleSizeButton) toggleSizeButton.hide();
            } else {
                if (configControl) configControl.show();
                if (playbackRateMenuButton) playbackRateMenuButton.hide();
                if (qualitySelector) qualitySelector.hide();
                if (toggleSizeButton) toggleSizeButton.show();
            }
        });

        addTextTracks();

        player.on('play', handlePlayEvent);
        player.on('pause', handlePauseEvent);
        player.on('timeupdate', handleTimeUpdateEvent);
        player.on('ended', handleEndedEvent);
        player.on('contextmenu', handleContextMenuEvent);
        player.on('qualitySelected', handleQualitySelected);

        checkForTimeChangeRequest();
    }

    const isReady = hasPlayback && Boolean(mediaStreams);

    if (error) {
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    }

    return (
        <div
            className={classNames(
                'MediaElement',
                className,
                `MediaElement--${aspectRatio}`,
                {
                    'MediaElement--video': interview.media_type === 'video',
                    'MediaElement--audio': interview.media_type === 'audio',
                    'MediaElement--loading': !isReady,
                    'MediaElement--mediaMissing': interview.media_missing,
                }
            )}
        >
            {hasPlayback && interview.media_missing ? (
                <p className="MediaMissing">{mediaMissingText}</p>
            ) : (
                <>
                    {isReady ? (
                        <VideoJS
                            type={interview.media_type}
                            options={videoJsOptions}
                            onReady={handlePlayerReady}
                            onEnded={handleEndedEvent}
                        />
                    ) : (
                        <div
                            className="MediaElement-placeholder"
                            aria-hidden="true"
                        >
                            {/* Reserve the same aspect ratio and minimum width while media is loading */}
                            <Spinner />
                        </div>
                    )}
                    <div
                        ref={resizeHandleRef}
                        title={t('media_player.resize_handle_tooltip')}
                        aria-label={t('media_player.resize_handle_tooltip')}
                        data-tooltip={t('media_player.resize_handle_tooltip')}
                        role="separator"
                        className={classNames('MediaElement-resizeHandle', {
                            'is-dragging': isDragging,
                        })}
                    />
                </>
            )}
        </div>
    );
}

MediaElement.propTypes = {
    archiveId: PropTypes.string.isRequired,
    className: PropTypes.string,
    interview: PropTypes.object.isRequired,
    tape: PropTypes.number,
    isPlaying: PropTypes.bool,
    timeChangeRequestAvailable: PropTypes.bool,
    timeChangeRequest: PropTypes.number,
    updateIsPlaying: PropTypes.func.isRequired,
    updateMediaTime: PropTypes.func.isRequired,
    resetMedia: PropTypes.func.isRequired,
    sendTimeChangeRequest: PropTypes.func.isRequired,
    clearTimeChangeRequest: PropTypes.func.isRequired,
};
